import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminNav from './AdminNav';

const CUSTOMER_REJECTION_REASON = 'Customer rejected the offer';

const OUTCOME_LABELS = {
  not_sent: 'admin.conversations.outcomeNotSent',
  sent: 'admin.conversations.outcomeSent',
  customer_cancelled: 'admin.conversations.outcomeCustomerCancelled',
  business_accepted: 'admin.conversations.outcomeBusinessAccepted',
  business_rejected: 'admin.conversations.outcomeBusinessRejected'
};

function formatViewDuration(ms, t) {
  const totalSeconds = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return t('admin.conversations.durationMinutes', { minutes, seconds });
  }
  return t('admin.conversations.durationSeconds', { seconds });
}

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

const OfferActivityCard = ({ offer, t }) => {
  const outcomeKey = OUTCOME_LABELS[offer.outcome] || OUTCOME_LABELS.not_sent;
  const showReason = offer.rejectionReason && offer.rejectionReason !== CUSTOMER_REJECTION_REASON;
  const contactValue = offer.infoFilledAt
    ? t('admin.conversations.filledAt', { date: formatWhen(offer.infoFilledAt) })
    : offer.hasContactDetails
      ? t('admin.conversations.yes')
      : t('admin.conversations.notFilled');

  return (
    <div className="border border-blue-100 rounded-lg p-4 bg-blue-50/40">
      <div className="text-sm font-semibold text-gray-900 mb-3">
        {t('admin.conversations.activityTitle')}
        {offer.totalAmount != null && (
          <span className="ml-2 text-xs font-medium text-gray-500">
            {t('admin.conversations.amount', { amount: Number(offer.totalAmount).toFixed(2) })}
          </span>
        )}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div>
          <dt className="text-xs text-gray-500">{t('admin.conversations.opened')}</dt>
          <dd className="text-gray-900">
            {offer.openedAt
              ? t('admin.conversations.openedAt', { date: formatWhen(offer.openedAt), count: offer.openCount || 1 })
              : t('admin.conversations.notOpened')}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">{t('admin.conversations.read')}</dt>
          <dd className="text-gray-900">
            {offer.readAt
              ? t('admin.conversations.readAt', { date: formatWhen(offer.readAt) })
              : t('admin.conversations.notRead')}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">{t('admin.conversations.timeInOffer')}</dt>
          <dd className="text-gray-900">{formatViewDuration(offer.viewDurationMs, t)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">{t('admin.conversations.locationDetails')}</dt>
          <dd className="text-gray-900">{offer.hasLocationDetails ? t('admin.conversations.yes') : t('admin.conversations.no')}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">{t('admin.conversations.contactDetails')}</dt>
          <dd className="text-gray-900">{contactValue}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">{t('admin.conversations.outcome')}</dt>
          <dd className="text-gray-900">
            {t(outcomeKey)}
            {offer.submittedAt && (offer.outcome === 'sent' || offer.outcome === 'business_accepted' || offer.outcome === 'business_rejected') && (
              <span className="block text-xs text-gray-500">
                {t('admin.conversations.submittedAt', { date: formatWhen(offer.submittedAt) })}
              </span>
            )}
            {showReason && (
              <span className="block text-xs text-gray-500">
                {t('admin.conversations.rejectionReason', { reason: offer.rejectionReason })}
              </span>
            )}
          </dd>
        </div>
      </dl>
      {offer.viewPath && (
        <div className="mt-3">
          <Link
            to={offer.viewPath}
            className="inline-flex text-sm font-medium text-blue-700 hover:text-blue-900"
          >
            {t('admin.conversations.viewOffer')}
          </Link>
        </div>
      )}
    </div>
  );
};

const AdminConversations = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, logout } = useAuth();
  const [chatbots, setChatbots] = useState([]);
  const [query, setQuery] = useState('');
  const [chatbotId, setChatbotId] = useState('');
  const [role, setRole] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedConversationId, setExpandedConversationId] = useState(null);
  const [fullConversation, setFullConversation] = useState(null);
  const [isThreadLoading, setIsThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');

  const highlightText = (text, term) => {
    const trimmed = term.trim();
    if (!trimmed) return text;
    const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 px-0.5 rounded">
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  useEffect(() => {
    fetchChatbots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchChatbots = async () => {
    try {
      const response = await fetch('/api/admin/chatbots', {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (!response.ok) {
        setChatbots([]);
        return;
      }

      const data = await response.json();
      const chatbotsArray = Array.isArray(data) ? data : (data.chatbots || []);
      setChatbots(chatbotsArray);
    } catch (err) {
      console.error('Error fetching chatbots:', err);
      setChatbots([]);
    }
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    const trimmed = query.trim();
    if (trimmed) params.set('query', trimmed);
    if (chatbotId) params.set('chatbotId', chatbotId);
    if (role) params.set('role', role);
    params.set('limit', '50');
    return params.toString();
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setError('');
    setResults([]);
    setExpandedConversationId(null);
    setFullConversation(null);
    setThreadError('');

    try {
      const response = await fetch(`/api/admin/conversations/search?${buildQueryParams()}`, {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        setError(data.error || t('admin.conversations.searchFailed'));
        return;
      }

      setResults(Array.isArray(data.results) ? data.results : []);
    } catch (err) {
      console.error('Error searching conversations:', err);
      setError(t('admin.conversations.searchFailedRetry'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleConversation = async (conversationId) => {
    if (expandedConversationId === conversationId) {
      setExpandedConversationId(null);
      setFullConversation(null);
      setThreadError('');
      return;
    }

    setExpandedConversationId(conversationId);
    setFullConversation(null);
    setThreadError('');
    setIsThreadLoading(true);

    try {
      const response = await fetch(`/api/admin/conversations/${conversationId}`, {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        setThreadError(data.error || t('admin.conversations.failedToLoadConversation'));
        return;
      }

      setFullConversation(data);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setThreadError(t('admin.conversations.failedToLoadConversation'));
    } finally {
      setIsThreadLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.conversations.title')}</h1>
          <p className="text-gray-600 mt-2">{t('admin.conversations.subtitle')}</p>
        </div>

        <AdminNav />

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.conversations.query')}</label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('admin.conversations.queryPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.conversations.chatbot')}</label>
              <select
                value={chatbotId}
                onChange={(e) => setChatbotId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">{t('admin.conversations.allChatbots')}</option>
                {chatbots.map((cb) => (
                  <option key={cb.id} value={cb.id}>
                    {cb.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.conversations.role')}</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">{t('admin.conversations.any')}</option>
                <option value="user">{t('admin.conversations.user')}</option>
                <option value="assistant">{t('admin.conversations.assistant')}</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Search size={16} />
              {isLoading ? t('admin.conversations.searching') : t('admin.conversations.search')}
            </button>
            {error && <span className="text-sm text-red-600">{error}</span>}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.conversations.results')}</h2>
          {results.length === 0 ? (
            <p className="text-sm text-gray-500">{t('admin.conversations.noResults')}</p>
          ) : (
            <div className="space-y-3">
              {results.map((item, index) => (
                <div key={`${item.conversationId}-${index}`} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="font-medium text-gray-700">{item.chatbotName || item.chatbotId}</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">{t('admin.conversations.conversation', { id: item.conversationId })}</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      {t('admin.conversations.updated', { date: new Date(item.updatedAt).toLocaleString() })}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleToggleConversation(item.conversationId)}
                      className="ml-auto text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      {expandedConversationId === item.conversationId ? t('admin.conversations.hideFullThread') : t('admin.conversations.viewFullThread')}
                    </button>
                  </div>
                  <div className="text-xs font-semibold text-gray-600 mb-2">
                    {t('admin.conversations.matchedMessages', { count: (item.messages || []).length })}
                  </div>
                  <div className="space-y-2">
                    {(item.messages || []).filter((message) => message.role !== 'system').map((message, msgIndex) => (
                      <div key={`${item.conversationId}-msg-${msgIndex}`} className="border border-gray-100 rounded p-3 bg-gray-50">
                        <div className="text-xs text-gray-500 mb-1">{t('admin.conversations.roleLabel', { role: message.role })}</div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {highlightText(message.content, query)}
                        </p>
                      </div>
                    ))}
                  </div>
                  {expandedConversationId === item.conversationId && (
                    <div className="mt-4 border-t border-gray-200 pt-4 bg-white">
                      <div className="text-sm font-semibold text-gray-900 mb-1">{t('admin.conversations.fullConversation')}</div>
                      <div className="text-xs text-gray-500 mb-3">
                        {t('admin.conversations.showingAllMessages')}
                      </div>
                      {isThreadLoading && (
                        <p className="text-sm text-gray-500">{t('admin.conversations.loadingConversation')}</p>
                      )}
                      {threadError && (
                        <p className="text-sm text-red-600">{threadError}</p>
                      )}
                      {fullConversation && fullConversation.conversationId === item.conversationId && (
                        <div className="space-y-2">
                          {(fullConversation.offers || []).length === 0 ? (
                            <p className="text-sm text-gray-500">{t('admin.conversations.noOffers')}</p>
                          ) : (
                            (fullConversation.offers || []).map((offer) => (
                              <OfferActivityCard key={offer.id} offer={offer} t={t} />
                            ))
                          )}
                          {(fullConversation.messages || []).filter((message) => message.role !== 'system').map((message, msgIndex) => (
                            <div key={`${fullConversation.conversationId}-full-${msgIndex}`} className="border border-gray-100 rounded p-3">
                              <div className="text-xs text-gray-500 mb-1">{t('admin.conversations.roleLabel', { role: message.role || 'unknown' })}</div>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                {highlightText(message.content, query)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminConversations;
